import { JSONContent } from "@tiptap/react";
import React, { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast/headless";

import {
  FIXTURES_ENABLED,
  getFixtureWorkoutDocuments,
} from "../../fixtures";
import {
  createWorkoutDocument,
  deleteWorkoutDocument,
  fetchWorkoutDocument,
  fetchWorkoutDocuments,
  updateWorkoutDocument,
} from "../../services/workout-documents";
import { getEmptyWorkoutDoc } from "@/utils/workouts";
import { ParsedWorkoutDocument, WorkoutDocument } from "@/types";
import { getWorkoutFromDocument } from "@/utils/workouts/document-to-workout";
import { useAuth } from "@/contexts/auth-context";

type WorkoutContextType =
  | undefined
  | {
      createWorkout: (data?: JSONContent) => Promise<{ id: string }>;
      updateWorkout: (
        id: string,
        data: JSONContent,
        metadata?: Partial<WorkoutDocument> | undefined
      ) => Promise<void>;
      deleteWorkout: (workoutId: string) => Promise<void>;
      getWorkouts: () => Promise<void>;
      getWorkout: (workoutId: string) => Promise<ParsedWorkoutDocument>;
      workoutDocs: ParsedWorkoutDocument[];
      mostRecentWorkoutDoc?: ParsedWorkoutDocument;
      loading: boolean;
    };

const WorkoutContext = React.createContext<WorkoutContextType>(undefined);
// Exported for the logged-out landing-page demo, which feeds the real workout
// components a static in-memory value instead of the firestore-backed one.
export { WorkoutContext };
export type { WorkoutContextType };

export function useWorkouts() {
  const context = useContext(WorkoutContext);
  if (context === undefined) {
    throw new Error(
      "The component using the the Workouts context must be a descendant of the context provider"
    );
  }
  return context;
}

type WorkoutsProviderProps = {
  children?: React.ReactNode;
};

export const WorkoutsProvider = ({ children }: WorkoutsProviderProps) => {
  const [workoutDocs, setWorkoutDocs] = useState<Array<ParsedWorkoutDocument>>(
    []
  );
  const [mostRecentWorkoutDoc, setMostRecentWorkoutDoc] =
    useState<ParsedWorkoutDocument>();
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (currentUser !== null)
      getWorkouts().catch((error) => {
        toast.error("Failed to load workout history");
        console.error(error);
      });
  }, [currentUser]);

  const getWorkouts = async () => {
    setLoading(true);
    const newWorkoutDocs = await fetchWorkouts();
    setWorkoutDocs(newWorkoutDocs);
    setLoading(false);
  };

  const getWorkout = async (workoutId: string) => {
    let localDoc = workoutDocs.find(
      (workoutDoc) => workoutDoc.id === workoutId
    );
    if (localDoc !== undefined) {
      setMostRecentWorkoutDoc(localDoc);
      return localDoc;
    }
    if (FIXTURES_ENABLED) {
      // Direct URL load can beat the list fetch; resolve from fixtures.
      const fixtureDoc = getFixtureWorkoutDocuments().find(
        (d) => d.id === workoutId
      );
      if (!fixtureDoc) throw new Error("Workout not found in fixtures");
      const parsed = {
        ...fixtureDoc,
        workout: getWorkoutFromDocument(fixtureDoc.data),
      };
      setMostRecentWorkoutDoc(parsed);
      return parsed;
    }
    const doc = await fetchWorkoutDocument(workoutId).then((doc) => {
      return {
        ...doc,
        workout: getWorkoutFromDocument(doc.data),
      };
    });
    setMostRecentWorkoutDoc(doc);
    return doc;
  };

  const fetchWorkouts = async () => {
    const docs = FIXTURES_ENABLED
      ? getFixtureWorkoutDocuments()
      : await fetchWorkoutDocuments();
    return docs.map((doc) => ({
      ...doc,
      workout: getWorkoutFromDocument(doc.data),
    }));
  };

  const createWorkout = async (data?: JSONContent) => {
    const doc = FIXTURES_ENABLED
      ? {
          id: `fx-new-${Date.now()}`,
          created: new Date(),
          data: data || getEmptyWorkoutDoc(),
          isTemplate: false,
          isShared: false,
          isEditable: true,
        }
      : await createWorkoutDocument(data);
    const workout = {
      ...doc,
      workout: getWorkoutFromDocument(doc.data),
    };
    setWorkoutDocs([workout, ...workoutDocs]);
    return workout;
  };

  const updateWorkout = async (
    id: string,
    data: JSONContent,
    metadata: Partial<WorkoutDocument> = {}
  ) => {
    const index = workoutDocs.findIndex((workoutDoc) => workoutDoc.id === id);
    if (index !== -1) {
      workoutDocs[index] = {
        ...workoutDocs[index],
        ...metadata,
        data,
        workout: getWorkoutFromDocument(data),
      };
      setWorkoutDocs([...workoutDocs]);
    }
    if (!FIXTURES_ENABLED) await updateWorkoutDocument(id, data, metadata);
  };

  const deleteWorkout = async (workoutId: string) => {
    if (!FIXTURES_ENABLED) await deleteWorkoutDocument(workoutId);
    const newWorkoutDocs = workoutDocs.filter(
      (workout) => workout.id !== workoutId
    );
    setWorkoutDocs([...newWorkoutDocs]);
  };

  const contextValue = {
    createWorkout,
    updateWorkout,
    deleteWorkout,
    getWorkouts,
    getWorkout,
    workoutDocs,
    mostRecentWorkoutDoc,
    loading,
  };

  return (
    <WorkoutContext.Provider value={contextValue}>
      {children}
    </WorkoutContext.Provider>
  );
};
